import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { type ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { Breadcrumb } from '@/components/Breadcrumb';
import type { CardProps } from '@/components/Cms/Card/types';
import type { ContentGroupProps } from '@/components/Cms/ContentGroup';
import { Contact } from '@/components/Cms/PlaceholderService/components/Contact';
import { SectionHeader } from '@/components/SectionHeader';
import { FixedPageDocument } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import type { ContactHit } from '@/integrations/algolia/types';
import type { ContentfulFixedPageCMSData } from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { paths } from '@/lib/paths';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';

import { getServiceContactHeadingText } from './helpers';

import * as S from './ServiceContactView.styles';

export interface ServiceContactViewProps {
  cms?: ContentfulFixedPageCMSData;
  contactInfo: ContactHit;
  country: { code: string; slug: string };
}

export const ServiceContactView = ({
  cms,
  contactInfo,
  country,
}: ServiceContactViewProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const router = useRouter();
  const { formatMessage } = useIntl();

  const itemsArray = ((liveCms?.bottomContent?.[0] as ContentGroupProps)
    ?.itemsCollection?.items || []) as CardProps[];
  const getItemById = (id: string) => itemsArray.find((item) => item.id === id);

  const breadcrumbItems = [
    {
      label: formatMessage({ id: 'common.home', defaultMessage: 'Home' }),
      target: paths.home.url(),
    },
    {
      label: formatMessage({ id: 'service.label', defaultMessage: 'Service' }),
      target: paths.service.url(),
    },
    {
      label: formatMessage({
        id: 'service.contactUs',
        defaultMessage: 'Contact Us',
      }),
      target: router.asPath,
    },
  ];

  const handleCallBreitlingClick = () => {
    const phoneNumber = contactInfo.phone_number;
    window.location.href = `tel:${phoneNumber}`;
  };
  const handleEmailClick = () => {
    const countryEmailAddress = contactInfo.email;
    window.location.href = `mailto:${countryEmailAddress}`;
  };
  const handleAppointmentClick = () => {
    const appointmentUrl = paths.stores.appointment.asPath({
      country: country.code,
    });
    router.push(appointmentUrl);
  };
  const handleFindABoutiqueClick = () => {
    const boutiqueUrl = paths.stores.breitlingBoutiques.asPath({
      country: country.code,
    });
    router.push(boutiqueUrl);
  };
  const handleFaqClick = () => {
    const faqUrl = paths.service.faq.url();
    router.push(faqUrl);
  };
  return (
    <>
      <S.BreadcrumbWrapper>
        <Breadcrumb.Wrapper>
          <Breadcrumb items={breadcrumbItems} />
        </Breadcrumb.Wrapper>
      </S.BreadcrumbWrapper>

      <S.Wrapper>
        <S.HeaderWrapper>
          <SectionHeader
            color='black'
            colorized={false}
            sizeDesktop={50}
            sizeMobile={40}
            text={getServiceContactHeadingText(liveCms, formatMessage)}
            textAlign='center'
          />
        </S.HeaderWrapper>
        <S.CardsWrapper>
          <Contact
            contentfulData={getItemById('call')}
            onClick={handleCallBreitlingClick}
            phoneNumber={contactInfo.phone_number}
          />
          <Contact
            contentfulData={getItemById('email')}
            email={contactInfo.email}
            onClick={handleEmailClick}
          />
          <Contact
            contentfulData={getItemById('appointment')}
            onClick={handleAppointmentClick}
          />
          <S.DoubleBoxContainer>
            <Contact
              contentfulData={getItemById('boutique')}
              onClick={handleFindABoutiqueClick}
            />
            <Contact
              contentfulData={getItemById('faq')}
              onClick={handleFaqClick}
            />
          </S.DoubleBoxContainer>
        </S.CardsWrapper>
      </S.Wrapper>
    </>
  );
};

ServiceContactView.getLayout = (page: ReactElement) => {
  return (
    <DraftModeContext.Provider value={page.props.draftMode}>
      <Default cmsData={page.props.cmsDefaultLayoutData}>{page}</Default>
    </DraftModeContext.Provider>
  );
};
